import {
  getPendingEnvelopes,
  recoverStranded,
  markInFlight,
  markAcked,
  markRejected,
  scheduleRetry,
  countPending,
} from "../local/repositories";
import { getMeta, setMeta, META_KEYS } from "../local/db";
import { SYNC_BATCH_LIMIT } from "../schemas";
import { applyChanges, type ServerChange } from "./pull";
import { measureSkew, recordSkew, type ClockReading } from "./clock";

/**
 * The outbox drain.
 *
 * Background only. Nothing on the sale path waits for this — a sale is already
 * committed locally and confirmed to the staff member before the drain ever
 * looks at it (Principle I).
 *
 * The batch cap mirrors the server's, and both derive from the same free-plan
 * limit of 50 D1 queries per Worker invocation.
 */

export interface SyncOutcome {
  attempted: number;
  applied: number;
  duplicate: number;
  rejected: number;
  pending: number;
  error?: string;
  /** Present only when the terminal clock is measurably wrong (FR-050). */
  clockSkew?: ClockReading;
}

interface ServerResult {
  opId: string;
  status: "applied" | "duplicate" | "rejected" | "deferred";
  error?: { code: string; message: string };
}

let running = false;

export async function drainOutbox(ignoreBackoff = false): Promise<SyncOutcome> {
  // A second drain while one is in flight would re-send envelopes already
  // marked in_flight. The server would dedupe them correctly, but the round
  // trips are wasted quota.
  if (running) {
    return { attempted: 0, applied: 0, duplicate: 0, rejected: 0, pending: await countPending() };
  }
  running = true;

  try {
    const terminalId = await getMeta<string>(META_KEYS.terminalId);
    if (!terminalId) {
      return { attempted: 0, applied: 0, duplicate: 0, rejected: 0, pending: 0 };
    }

    // Recover anything stranded in_flight by an interrupted request. Safe to
    // re-send: the server dedupes by op_id, and the risk of NOT re-sending is a
    // sale that never reaches the books.
    await recoverStranded();

    const envelopes = await getPendingEnvelopes(SYNC_BATCH_LIMIT, ignoreBackoff);
    if (envelopes.length === 0) {
      return { attempted: 0, applied: 0, duplicate: 0, rejected: 0, pending: 0 };
    }

    const cursor = (await getMeta<number>(META_KEYS.syncCursor)) ?? 0;
    await markInFlight(envelopes.map((e) => e.opId));

    let response: Response;
    try {
      response = await fetch("/api/sync", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          terminalId,
          cursor,
          envelopes: envelopes.map((e) => ({
            opId: e.opId,
            type: e.type,
            createdAt: e.createdAt,
            payload: e.payload,
          })),
        }),
      });
    } catch (error) {
      // Network failure. Everything goes back to pending with backoff — this is
      // the normal case during an outage, not an error condition.
      for (const envelope of envelopes) {
        await scheduleRetry(envelope.opId, envelope.attempts + 1);
      }
      return {
        attempted: envelopes.length,
        applied: 0,
        duplicate: 0,
        rejected: 0,
        pending: await countPending(),
        error: error instanceof Error ? error.message : "Network unavailable",
      };
    }

    if (!response.ok) {
      for (const envelope of envelopes) {
        await scheduleRetry(envelope.opId, envelope.attempts + 1);
      }
      return {
        attempted: envelopes.length,
        applied: 0,
        duplicate: 0,
        rejected: 0,
        pending: await countPending(),
        error: `Server returned ${response.status}`,
      };
    }

    const body = (await response.json()) as {
      results: ServerResult[];
      changes?: ServerChange[];
      cursor: number;
      serverTime: string;
    };

    let applied = 0;
    let duplicate = 0;
    let rejected = 0;

    for (const result of body.results) {
      switch (result.status) {
        case "applied":
          applied += 1;
          await markAcked(result.opId, body.serverTime);
          break;

        case "duplicate":
          // SUCCESS, not an error. The server already has this exact operation,
          // which is precisely what idempotency is for.
          duplicate += 1;
          await markAcked(result.opId, body.serverTime);
          break;

        case "rejected": {
          // Permanently invalid. Retained and surfaced to staff — never
          // silently dropped (FR-049), because a rejected sale is a real event
          // that happened in the café and someone has to reconcile it.
          rejected += 1;
          await markRejected(result.opId, result.error?.message ?? "Rejected by server");
          break;
        }

        case "deferred": {
          const envelope = envelopes.find((e) => e.opId === result.opId);
          await scheduleRetry(result.opId, (envelope?.attempts ?? 0) + 1);
          break;
        }
      }
    }

    // Server changes are applied BEFORE the cursor advances, so a failure
    // midway replays them rather than skipping them.
    const pull = await applyChanges(body.changes ?? []);
    await setMeta(META_KEYS.syncCursor, Math.max(body.cursor, pull.cursor));

    // Measured, recorded, never used to correct a timestamp. A wrong clock
    // means the sales are misplaced in time, and erasing the evidence of that
    // would be worse than the skew (FR-050).
    const skew = measureSkew(body.serverTime);
    await recordSkew(skew);

    return {
      attempted: envelopes.length,
      applied,
      duplicate,
      rejected,
      pending: await countPending(),
      ...(skew.severity === "ok" ? {} : { clockSkew: skew }),
    };
  } finally {
    running = false;
  }
}

/**
 * Drain repeatedly until the outbox is empty.
 *
 * Used on reconnect after a long outage, where a single 25-envelope batch would
 * leave hundreds behind.
 */
export async function drainUntilEmpty(maxBatches = 40): Promise<SyncOutcome> {
  const total: SyncOutcome = { attempted: 0, applied: 0, duplicate: 0, rejected: 0, pending: 0 };

  for (let i = 0; i < maxBatches; i++) {
    const outcome = await drainOutbox(true);
    total.attempted += outcome.attempted;
    total.applied += outcome.applied;
    total.duplicate += outcome.duplicate;
    total.rejected += outcome.rejected;
    total.pending = outcome.pending;

    if (outcome.error) {
      total.error = outcome.error;
      break;
    }
    if (outcome.attempted === 0) break;
  }

  return total;
}
